import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

// Step components
import InductionStep from '../components/onboarding/InductionStep';
import FormsStep from '../components/onboarding/FormsStep';
import TrainingStep from '../components/onboarding/TrainingStep';
import ProcessDocsStep from '../components/onboarding/ProcessDocsStep';
import CompletionStep from '../components/onboarding/CompletionStep';

const steps = [
  'Induction',
  'Forms',
  'Process Documents',
  'Training',
  'Completion',
];

const Onboarding = () => {
  const { user, getAuthHeaders } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isStepValid, setIsStepValid] = useState(false);
  const [validationTrigger, setValidationTrigger] = useState(0);

  const [formData, setFormData] = useState({
    induction: { companyVideo: false, policies: false, handbook: false },
    forms: { personalInfo: {}, documents: [] },
    training: { completed: [], inProgress: [] },
    processDocs: { submitted: [], pending: [] },
    completion: { checklist: {} },
    accessProvisioning: { systems: [], status: {} },
    ssoIntegration: { status: 'pending' },
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Connectors use flex: 1 so spacing adapts to the container width responsively

  // Fetch current step on load
  useEffect(() => {
    const fetchCurrentStep = async () => {
      if (!user || !user.id) return;
      try {
        const response = await fetch(`${API_URL}/onboarding/current-step/${user.id}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        const data = await response.json();

        // If all steps are completed, set activeStep to the last step (4)
        // Otherwise, use the currentStep from backend
        const currentStep = data.currentStep >= steps.length ? steps.length - 1 : data.currentStep;
        setActiveStep(currentStep);

        const completedIndexes = data.completedModules
          ?.map((module) => steps.indexOf(module))
          .filter((index) => index !== -1);

        console.log('=== BACKEND DATA DEBUG ===');
        console.log('Backend response:', data);
        console.log('Completed modules:', data.completedModules);
        console.log('Steps array:', steps);
        console.log('Completed indexes:', completedIndexes);
        console.log('Setting completedSteps to:', completedIndexes || []);
        console.log('===========================');
        setCompletedSteps(completedIndexes || []);
      } catch (error) {
        console.error('Error fetching current step:', error);
      }
    };

    fetchCurrentStep();
  }, [user, getAuthHeaders]);

  // Validate step on change
  useEffect(() => {
    const checkStepValidity = async () => {
      if (!user || !user.id) return;
      const valid = await validateStep(activeStep);
      setIsStepValid(valid);
    };
    checkStepValidity();
  }, [activeStep, formData, user, validationTrigger]);

  // Manual validation trigger function
  const triggerValidation = () => {
    setValidationTrigger(prev => prev + 1);
  };

  const handleNext = async () => {
    setLoading(true);
    try {
      const isValid = await validateStep(activeStep);
      if (!isValid) {
        toast.error('Please complete all required fields in the current step');
        setLoading(false);
        return;
      }

      await saveStepData(activeStep);

      setCompletedSteps((prev) => [...new Set([...prev, activeStep])]);
      
      // If this is the final step (Completion), don't go to next step
      if (activeStep === 4) {
        toast.success('Onboarding completed successfully!');
        // You can redirect to dashboard or show completion message here
      } else {
        setActiveStep((prevStep) => prevStep + 1);
        toast.success('Step completed successfully!');
      }
    } catch (error) {
      toast.error('Error saving step data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle step click - only allow navigation to completed steps or current step
  const handleStepClick = async (stepIndex) => {
    console.log('handleStepClick called:', {
      stepIndex,
      activeStep,
      completedSteps,
      stepName: steps[stepIndex]
    });
    
    // Allow clicking on current step
    if (stepIndex === activeStep) {
      console.log('Clicked on current step');
      return;
    }

    // Allow clicking on completed steps
    if (completedSteps.includes(stepIndex)) {
      console.log('Clicked on completed step, navigating to:', stepIndex);
      setActiveStep(stepIndex);
      return;
    }

    // Allow clicking on the next step if current step is completed
    if (stepIndex === activeStep + 1) {
      const isCurrentStepValid = await validateStep(activeStep);
      if (isCurrentStepValid) {
        await saveStepData(activeStep);
        setCompletedSteps((prev) => [...new Set([...prev, activeStep])]);
        setActiveStep(stepIndex);
        toast.success('Step completed successfully!');
      } else {
        toast.error('Please complete the current step before proceeding');
      }
      return;
    }

    // For other steps, show a message
    toast.error('Please complete the previous steps before accessing this step');
  };

  const validateStep = async (step) => {
    switch (step) {
      case 0: {
        try {
          const response = await fetch(`${API_URL}/onboarding/Induction?userId=${user.id}`, {
            method: 'GET',
            headers: getAuthHeaders(),
          });
          if (!response.ok) throw new Error(`API error: ${response.status}`);
          const result = await response.json();
          return result.completed === true;
        } catch (error) {
          console.error('Error validating induction step:', error);
          return false;
        }
      }
      case 1: {
        try {
          const response = await fetch(`${API_URL}/onboarding/onboarding-form/${user.id}`, {
            method: 'GET',
            headers: getAuthHeaders(),
          });
          if (!response.ok) throw new Error(`API error: ${response.status}`);
          const result = await response.json();
          // Return true if form data exists (not null)
          return result.success === true && result.data !== null;
        } catch (error) {
          console.error('Error validating onboarding forms step:', error);
          return false;
        }
      }
      case 2: {
        try {
          // Simple validation: check if all required documents are uploaded
          const response = await fetch(`${API_URL}/onboarding/process-docs-validation/${user.id}`, {
            headers: getAuthHeaders(),
          });
          
          if (!response.ok) throw new Error(`API error: ${response.status}`);
          const result = await response.json();
          
          if (!result.success) {
            toast.error('Failed to validate documents');
            return false;
          }
          
          // Return simple true/false result
          return result.isValid;
        } catch (error) {
          console.error('Error validating process docs step:', error);
          return false;
        }
    
      }
      case 3: {
        const { completed } = formData.training;
        return true;
      }
      case 4: {
        const { checklist } = formData.completion;
        const values = Object.values(checklist);
        return values.length > 0 && values.every((val) => val === true);
      }
      default:
        return true;
    }
  };

  const saveStepData = async (step) => {
    const module = steps[step];
    try {
      await fetch(`${API_URL}/onboarding/save-step`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userId: user.id,
          module,
          completed: true,
        }),
      });
    } catch (error) {
      console.error('Error saving step data to backend:', error);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <InductionStep data={formData.induction} onChange={(data) => setFormData((prev) => ({ ...prev, induction: data }))} />;
      case 1:
        return <FormsStep data={formData.forms} onChange={(data) => setFormData((prev) => ({ ...prev, forms: data }))} onFormSubmit={triggerValidation} />;
      case 2:
        return <ProcessDocsStep data={formData.processDocs} onChange={(data) => setFormData((prev) => ({ ...prev, processDocs: data }))} onFormSubmit={triggerValidation} />;
      case 3:
        return <TrainingStep data={formData.training} onChange={(data) => setFormData((prev) => ({ ...prev, training: data }))} />;
      case 4:
        return <CompletionStep data={formData} onChange={(data) => setFormData((prev) => ({ ...prev, completion: data }))} />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
      <Container maxWidth={false} disableGutters sx={{ width: '100%' }}>
        <Paper sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 , fontSize: '30px'}}>
            Employee Onboarding
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Complete all steps to finish your onboarding process.
          </Typography>

          <Box sx={{ overflowX: isMobile ? 'auto' : 'visible' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                minWidth: isMobile ? '100%' : '600px',
                my: 3,
              }}
            >
              {steps.map((label, index) => {
                const isCompleted = completedSteps.includes(index);
                const isCurrentStep = index === activeStep;
                const isAccessible = isCompleted || isCurrentStep || index === activeStep + 1;

                return (
                  <React.Fragment key={label}>
                    <Box
                      onClick={() => {
                        if (isAccessible) {
                          handleStepClick(index);
                        }
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isCompleted || isCurrentStep ? 'primary.main' : 'grey.300',
                        color: (isCompleted || isCurrentStep) ? 'white' : 'grey.600',
                        cursor: isAccessible ? 'pointer' : 'default',
                        boxShadow: isCurrentStep ? '0 0 0 4px rgba(126,87,194,0.2)' : 'none',
                        transition: 'background-color 0.2s ease',
                        flexShrink: 0,
                      }}
                    >
                      {isCompleted ? <CheckIcon sx={{ fontSize: 20 }} /> : null}
                    </Box>
                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          flex: 1,
                          height: 2,
                          backgroundColor: 'grey.300',
                          minWidth: 24,
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
            {/* Labels row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                width: '100%',
                minWidth: isMobile ? '100%' : '600px',
                mt: 1,
              }}
            >
              {steps.map((label, index) => {
                const isCompleted = completedSteps.includes(index);
                const isCurrentStep = index === activeStep;
                const isAccessible = isCompleted || isCurrentStep || index === activeStep + 1;

                return (
                  <React.Fragment key={`label-${label}`}>
                    <Box sx={{ width: 36, flex: '0 0 auto' }}>
                      <Typography
                        variant="body2"
                        onClick={() => {
                          if (isAccessible) {
                            handleStepClick(index);
                          }
                        }}
                        sx={{
                          width: 120,
                          ml: '-42px',
                          textAlign: 'center',
                          color: isCompleted
                            ? 'text.primary'
                            : isCurrentStep
                              ? 'primary.main'
                              : 'text.secondary',
                          cursor: isAccessible ? 'pointer' : 'default',
                          userSelect: 'none',
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    {index < steps.length - 1 && (
                      <Box sx={{ flex: 1, minWidth: 24 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ mt: 3, width: '100%', fontSize: '1rem' }}>
            {getStepContent(activeStep)}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              gap: 2,
              mt: 4
            }}
          >
            <Button
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
              variant="outlined"
              size="medium"
              fullWidth={isMobile}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading || !isStepValid || (activeStep === 4 && completedSteps.includes(4))}
              size="medium"
              fullWidth={isMobile}
              endIcon={loading && <CircularProgress size={18} color="inherit" />}
            >
              {activeStep === 4 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Onboarding;
